/**
 * 素材卡片组件
 * 用于展示单个商品素材的卡片
 * 展示图片、标题、价格
 */

import type { ReactElement } from 'react'
import { useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import { Trash2, Loader2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'
import type { Material } from '@/types/material'
import { deleteMaterial } from '@/services/materialService'

/**
 * 组件属性
 */
interface MaterialCardProps {
  /** 素材数据 */
  material: Material
  /** 刷新列表回调 */
  onRefresh?: () => void
}

/**
 * 素材卡片组件
 * @param props - 组件属性
 * @returns 素材卡片 ReactElement
 */
export function MaterialCard({
  material,
  onRefresh,
}: MaterialCardProps): ReactElement {
  // 删除确认弹窗状态
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  /**
   * 处理删除确认
   */
  const handleDeleteConfirm = useCallback(async (): Promise<void> => {
    setIsDeleting(true)
    setDeleteError(null)
    try {
      await deleteMaterial(material.id)
      setShowDeleteDialog(false)
      onRefresh?.()
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : '删除失败，请稍后再试')
    } finally {
      setIsDeleting(false)
    }
  }, [material.id, onRefresh])

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className={cn(
          'group flex flex-col overflow-hidden rounded-lg border border-border/60 bg-card',
          'shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all duration-200',
          'hover:border-border hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]'
        )}
      >
        {/* 商品图片 */}
        <PhotoProvider>
          <PhotoView src={material.imageUrl}>
            <div className="relative aspect-square cursor-pointer overflow-hidden bg-muted">
              <img
                src={material.imageUrl}
                alt={material.title}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              {/* 悬浮删除按钮 */}
              <div className="absolute right-1.5 top-1.5 opacity-0 transition-all duration-200 group-hover:opacity-100">
                <Button
                  variant="secondary"
                  size="icon"
                  className="h-7 w-7 rounded-md bg-white/95 shadow-sm backdrop-blur-sm transition-colors hover:bg-white"
                  onClick={(e): void => {
                    e.stopPropagation()
                    setShowDeleteDialog(true)
                  }}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-500" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  )}
                </Button>
              </div>
            </div>
          </PhotoView>
        </PhotoProvider>

        {/* 商品信息 */}
        <div className="flex flex-1 flex-col px-3 pb-3 pt-2.5">
          {/* 商品标题 */}
          <h3 className="line-clamp-2 flex-1 text-[13px] font-medium leading-snug text-foreground/90">
            {material.title}
          </h3>

          {/* 价格信息 */}
          <div className="mt-2">
            <span className="text-sm font-semibold tracking-tight text-primary">
              {material.price}
            </span>
          </div>
        </div>
      </motion.div>

      {/* 删除确认弹窗 */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除素材</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>确认要删除这个素材吗？删除后将无法恢复。</p>
                <div className="rounded-lg border bg-muted/50 p-3">
                  <p className="line-clamp-2 text-sm font-medium text-foreground">
                    {material.title}
                  </p>
                </div>
                {deleteError !== null && (
                  <div className="flex items-start gap-2 rounded-lg border border-red-500/20 bg-red-500/10 p-2 text-sm text-red-600">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{deleteError}</span>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e): void => {
                e.preventDefault()
                void handleDeleteConfirm()
              }}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  删除中...
                </>
              ) : (
                '确认删除'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
